import { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Star,
  ShieldCheck,
  Hammer,
  ClipboardCheck,
  BadgeCheck,
  Zap,
  LifeBuoy,
  Sparkles,
  Check,
} from "lucide-react";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { WhatsAppFloat } from "@/components/site/WhatsAppFloat";
import { QuoteDialog } from "@/components/site/QuoteDialog";
import { FinalCTA } from "@/components/site/FinalCTA";
import { CountUp } from "@/components/site/CountUp";
import { Reveal } from "@/components/site/Reveal";
import { RecentProjects } from "@/components/site/RecentProjects";
import { TrustedBy } from "@/components/site/TrustedBy";
import { SERVICES } from "@/lib/brand";

const HERO_IMG =
  "https://images.unsplash.com/photo-1628744876497-eb30460be9f6?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400";

const WHO_IMG =
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1600&q=85";

const WHY = [
  {
    icon: ShieldCheck,
    title: "Turnkey Solutions",
    body: "One contractor. One team. One point of contact. Complete project delivery from planning to final handover.",
  },
  {
    icon: Hammer,
    title: "Expert Craftsmanship",
    body: "Highly skilled specialists delivering exceptional workmanship using premium materials and industry best practices.",
  },
  {
    icon: ClipboardCheck,
    title: "Transparent Process",
    body: "Clear quotations. Defined timelines. Regular project updates. No hidden surprises.",
  },
  {
    icon: BadgeCheck,
    title: "Quality Assurance",
    body: "Every stage undergoes detailed quality inspections before project completion.",
  },
  {
    icon: Zap,
    title: "Fast Project Delivery",
    body: "Efficient planning and professional execution reduce downtime while maintaining exceptional quality.",
  },
  {
    icon: LifeBuoy,
    title: "Long-Term Support",
    body: "Our relationship doesn't end after handover. We provide maintenance, repairs and facility management.",
  },
];

const PROCESS = [
  { n: "01", title: "Consultation", body: "We understand your vision, inspect your property and identify requirements." },
  { n: "02", title: "Planning & Design", body: "Layouts, specifications, material selections and execution plans tailored to you." },
  { n: "03", title: "Quotation", body: "A transparent proposal outlining scope, timeline and pricing — no hidden costs." },
  { n: "04", title: "Execution", body: "Our specialists complete every stage with quality materials and strict project management." },
  { n: "05", title: "Quality Inspection", body: "Every detail is carefully inspected before delivery to ensure the highest standards." },
  { n: "06", title: "Handover & Support", body: "Your project is delivered ready for use with continued maintenance and aftercare." },
];

export default function Home() {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 120]);
  const heroScale = useTransform(scrollY, [0, 600], [1.05, 1.15]);
  const heroOpacity = useTransform(scrollY, [0, 400], [1, 0.5]);

  useEffect(() => {
    document.title = "Bloc & Brush | Premium Interior Fit-Out & Property Solutions in Dubai";
  }, []);

  return (
    <div className="bg-white">
      <Navbar onQuoteClick={() => setQuoteOpen(true)} />

      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-end overflow-hidden"
      >
        <motion.div
          style={{ y: heroY, scale: heroScale }}
          className="absolute inset-0"
        >
          <img
            src={HERO_IMG}
            alt="Luxury Dubai apartment under fit-out"
            className="w-full h-[120%] object-cover"
            loading="eager"
          />
        </motion.div>
        <motion.div
          style={{ opacity: heroOpacity }}
          className="absolute inset-0 bg-gradient-to-b from-[#0B1A33]/55 via-[#0B1A33]/40 to-[#0B1A33]/85"
        />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(19,41,75,0.0),_rgba(11,26,51,0.6))]" />

        <div className="relative container mx-auto px-6 lg:px-12 max-w-7xl pt-32 pb-20 lg:pb-28 text-white">
          <motion.span
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-xs lg:text-sm uppercase tracking-[0.28em] text-white/70"
          >
            Design. Build. Maintain. — Dubai
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.15 }}
            data-testid="hero-title"
            className="font-display mt-6 text-5xl md:text-6xl lg:text-[78px] font-extrabold leading-[0.98] tracking-tight max-w-5xl"
          >
            Transforming Spaces.
            <br />
            <span className="text-white/70">Building Experiences.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="mt-8 text-base lg:text-[18px] text-white/75 max-w-2xl leading-relaxed"
          >
            Bloc & Brush delivers premium Interior Fit-Out, Renovation, MEP,
            Facility Maintenance, Holiday Home Furnishing and Professional
            Cleaning services across Dubai — from concept to long-term care,
            under one trusted partner.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.45 }}
            className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4"
          >
            <button
              onClick={() => setQuoteOpen(true)}
              data-testid="hero-quote-btn"
              className="inline-flex items-center justify-center gap-2 bg-white text-[#13294B] hover:bg-white/90 rounded-full px-7 py-4 text-[15px] font-medium transition shadow-lg"
            >
              Get Free Quote <ArrowRight size={16} />
            </button>
            <Link
              to="/projects"
              data-testid="hero-projects-btn"
              className="inline-flex items-center justify-center gap-2 border border-white/30 hover:bg-white/10 rounded-full px-7 py-4 text-[15px] font-medium transition"
            >
              View Our Projects
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.6 }}
            className="mt-10 flex items-center gap-3 text-sm text-white/75"
          >
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={16} className="fill-white text-white" />
              ))}
            </div>
            <span className="max-w-md leading-relaxed">
              Trusted by homeowners, investors, Airbnb hosts and businesses
              throughout Dubai.
            </span>
          </motion.div>

          {/* Stats */}
          <div
            data-testid="hero-stats"
            className="mt-16 lg:mt-24 grid grid-cols-2 lg:grid-cols-4 gap-y-10 gap-x-6 border-t border-white/15 pt-12 max-w-5xl"
          >
            {[
              { v: 320, suf: "+", label: "Projects Completed" },
              { v: 280, suf: "+", label: "Satisfied Clients" },
              { v: 12, suf: " yrs", label: "Years Experience" },
              { v: 24, suf: "/7", label: "Emergency Support" },
            ].map((s) => (
              <div key={s.label}>
                <div className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight">
                  <CountUp end={s.v} suffix={s.suf} />
                </div>
                <div className="mt-2 text-xs uppercase tracking-[0.18em] text-white/60">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO WE ARE */}
      <section
        data-testid="who-section"
        className="py-24 lg:py-36 bg-white"
      >
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
            <Reveal className="lg:col-span-6 order-2 lg:order-1">
              <span className="text-xs uppercase tracking-[0.22em] text-[#707070]">
                Who we are
              </span>
              <h2 className="font-display mt-4 text-4xl lg:text-[48px] font-extrabold leading-[1.05] tracking-tight text-[#1C1C1C]">
                One Company.
                <br />
                <span className="text-[#13294B]">Complete Property Solutions.</span>
              </h2>
              <div className="mt-7 space-y-5 text-[17px] leading-[1.75] text-[#1C1C1C]/75">
                <p>
                  At Bloc & Brush we believe every property deserves exceptional
                  craftsmanship, intelligent design and reliable execution.
                </p>
                <p>
                  Unlike traditional contractors focused on a single discipline,
                  we deliver complete turnkey solutions under one roof — interior
                  fit-out, renovation, MEP, holiday home furnishing, authority
                  approvals, facility maintenance and professional cleaning.
                </p>
                <p>
                  Whether you're creating a luxury residence, upgrading an office
                  or preparing a holiday home for guests, we deliver spaces that
                  are functional, elegant and built to last.
                </p>
              </div>

              <div className="mt-10 flex items-center gap-6">
                <button
                  onClick={() => setQuoteOpen(true)}
                  data-testid="who-quote-btn"
                  className="inline-flex items-center gap-2 bg-[#13294B] hover:bg-[#0E1F3A] text-white rounded-full px-7 py-3.5 text-[15px] font-medium transition"
                >
                  Start your project <ArrowRight size={16} />
                </button>
                <Link
                  to="/about"
                  className="text-[15px] font-medium text-[#13294B] hover:underline underline-offset-4"
                >
                  About us →
                </Link>
              </div>
            </Reveal>

            <Reveal delay={0.15} className="lg:col-span-6 order-1 lg:order-2">
              <div className="relative">
                <div className="aspect-[4/5] overflow-hidden rounded-3xl">
                  <img
                    src={WHO_IMG}
                    alt="Luxury modern Dubai apartment interior"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1.4s] ease-out"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 lg:-left-10 bg-white shadow-[0_20px_60px_rgba(19,41,75,0.15)] rounded-2xl p-6 max-w-[260px]">
                  <div className="font-display text-3xl font-extrabold text-[#13294B]">
                    A–Z
                  </div>
                  <div className="text-sm text-[#707070] mt-1 leading-relaxed">
                    From concept and approvals to handover and maintenance.
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section
        data-testid="why-section"
        className="py-24 lg:py-32 bg-[#F5F5F5]"
      >
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <Reveal>
            <div className="max-w-3xl">
              <span className="text-xs uppercase tracking-[0.22em] text-[#707070]">
                Why Bloc & Brush
              </span>
              <h2 className="font-display mt-4 text-4xl lg:text-[48px] font-extrabold leading-[1.05] tracking-tight text-[#1C1C1C]">
                Why Dubai Chooses
                <br />
                <span className="text-[#13294B]">Bloc & Brush.</span>
              </h2>
            </div>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY.map((w, i) => {
              const Icon = w.icon;
              return (
                <Reveal key={w.title} delay={i * 0.06}>
                  <div
                    data-testid={`why-card-${i}`}
                    className="group h-full bg-white p-8 lg:p-10 rounded-2xl border border-black/5 hover:border-[#13294B]/30 hover:-translate-y-1.5 hover:shadow-[0_24px_50px_rgba(19,41,75,0.10)] transition-all duration-500 flex flex-col"
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#13294B]/5 flex items-center justify-center text-[#13294B] group-hover:bg-[#13294B] group-hover:text-white transition-colors duration-500">
                      <Icon size={22} strokeWidth={1.6} />
                    </div>
                    <h3 className="font-display mt-7 text-xl lg:text-[22px] font-bold tracking-tight text-[#1C1C1C]">
                      {w.title}
                    </h3>
                    <p className="mt-3 text-[15px] leading-relaxed text-[#1C1C1C]/65">
                      {w.body}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section
        data-testid="services-section"
        className="py-24 lg:py-36 bg-white"
      >
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <Reveal>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
              <div className="max-w-3xl">
                <span className="text-xs uppercase tracking-[0.22em] text-[#707070]">
                  Our Services
                </span>
                <h2 className="font-display mt-4 text-4xl lg:text-[48px] font-extrabold leading-[1.05] tracking-tight text-[#1C1C1C]">
                  Everything Your Property Needs.
                  <br />
                  <span className="text-[#13294B]">One Trusted Partner.</span>
                </h2>
              </div>
              <Link
                to="/services"
                className="text-[15px] font-medium text-[#13294B] hover:underline underline-offset-4"
              >
                All services →
              </Link>
            </div>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={(i % 2) * 0.1}>
                <article
                  data-testid={`service-card-${s.slug}`}
                  className="group relative overflow-hidden rounded-3xl bg-[#0B1A33] shadow-[0_10px_30px_rgba(0,0,0,0.06)] hover:shadow-[0_30px_60px_rgba(19,41,75,0.18)] transition-all duration-500"
                >
                  <div className="aspect-[16/11] overflow-hidden">
                    <img
                      src={s.image}
                      alt={s.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.06] transition-transform duration-[1.4s] ease-out"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B1A33] via-[#0B1A33]/40 to-transparent" />
                  <div className="absolute inset-0 flex flex-col justify-end p-8 lg:p-10 text-white">
                    <div className="text-xs uppercase tracking-[0.22em] text-white/55">
                      0{i + 1}
                    </div>
                    <h3 className="font-display mt-3 text-2xl lg:text-[28px] font-bold tracking-tight">
                      {s.title}
                    </h3>
                    <p className="mt-3 text-[15px] text-white/70 leading-relaxed max-w-md">
                      {s.description}
                    </p>
                    <Link
                      to="/services"
                      className="mt-5 inline-flex items-center gap-2 text-[14px] font-medium text-white group-hover:gap-3 transition-all"
                    >
                      Explore Service <ArrowUpRight size={16} />
                    </Link>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* RECENT PROJECTS */}
      <RecentProjects />

      {/* TRUSTED BY CLIENTS */}
      <TrustedBy />

      {/* PROCESS */}
      <section
        data-testid="process-section"
        className="py-24 lg:py-36 bg-[#0B1A33] text-white relative overflow-hidden"
      >
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <Reveal>
            <div className="max-w-3xl">
              <span className="text-xs uppercase tracking-[0.22em] text-white/55">
                Our Process
              </span>
              <h2 className="font-display mt-4 text-4xl lg:text-[48px] font-extrabold leading-[1.05] tracking-tight">
                A Seamless Experience
                <br />
                <span className="text-white/65">From Start to Finish.</span>
              </h2>
            </div>
          </Reveal>

          <div className="mt-16 relative">
            <div className="hidden lg:block absolute top-[44px] left-0 right-0 h-px bg-white/15" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 lg:gap-6">
              {PROCESS.map((p, i) => (
                <Reveal key={p.n} delay={i * 0.08}>
                  <div
                    data-testid={`process-step-${p.n}`}
                    className="relative"
                  >
                    <div className="flex items-center gap-3 lg:block">
                      <div className="relative">
                        <div className="w-3 h-3 rounded-full bg-white" />
                        <div className="absolute inset-0 rounded-full bg-white/30 animate-ping" />
                      </div>
                      <div className="font-display text-xs uppercase tracking-[0.22em] text-white/45 lg:mt-6">
                        Step {p.n}
                      </div>
                    </div>
                    <h3 className="font-display mt-4 text-xl lg:text-[22px] font-bold tracking-tight">
                      {p.title}
                    </h3>
                    <p className="mt-3 text-[14px] text-white/60 leading-relaxed">
                      {p.body}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal delay={0.2}>
            <div className="mt-16 flex items-center gap-4">
              <button
                onClick={() => setQuoteOpen(true)}
                data-testid="process-quote-btn"
                className="inline-flex items-center gap-2 bg-white text-[#13294B] hover:bg-white/90 rounded-full px-7 py-3.5 text-[15px] font-medium transition"
              >
                Begin your project <ArrowRight size={16} />
              </button>
              <div className="flex items-center gap-2 text-sm text-white/60">
                <Sparkles size={14} />
                <span>Average response within 24 hours</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <FinalCTA onQuoteClick={() => setQuoteOpen(true)} />
      <Footer onQuoteClick={() => setQuoteOpen(true)} />
      <WhatsAppFloat />
      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} />
    </div>
  );
}
